import { memo, useCallback, useEffect, useState } from 'react';

import { cn } from '../../lib/styles';

import { parseHexToRgb, rgbToHsv, tryCompleteHex } from './hexagonColorPicker.utils';
import { HexagonalColorHue } from './hexagonColorPicker/HexagonalColorHue';
import { HexagonalColorPicker } from './hexagonColorPicker/HexagonalColorPicker';

function hueFromHex(hex: string): number {
  const rgb = parseHexToRgb(tryCompleteHex(hex) ?? hex);
  if (!rgb) return 0;
  return rgbToHsv(rgb[0], rgb[1], rgb[2]).h;
}

type HexagonColorPickerProps = {
  color: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Wraps [crypto-red/react-hexagon-color-picker](https://github.com/crypto-red/react-hexagon-color-picker)
 * (ported here as functional TS) with hue strip + parent-controlled hex value.
 */
const HexagonColorPickerBase = ({ color, onChange, disabled, className }: HexagonColorPickerProps) => {
  const [hue, setHue] = useState(() => hueFromHex(color));

  useEffect(() => {
    setHue(hueFromHex(color));
  }, [color]);

  const handleColorChange = useCallback(
    (hex: string) => {
      onChange(hex);
    },
    [onChange],
  );

  const border = 'rgba(0,0,0,0.2)';
  const highlight = 'var(--color-border-input-select)';

  return (
    <div className={cn('flex w-full max-w-[234px] flex-col gap-2', className)}>
      <div className={cn(disabled && 'pointer-events-none opacity-50')}>
        <HexagonalColorPicker
          hue={hue}
          color={color}
          onColorChange={handleColorChange}
          border={border}
          highlight={highlight}
          className="block h-auto w-full"
        />
        <HexagonalColorHue
          hue={hue}
          onHueChange={setHue}
          border={border}
          highlight={highlight}
          className="mt-1 block h-auto w-full"
        />
      </div>
    </div>
  );
};

export const HexagonColorPicker = memo(HexagonColorPickerBase);
